import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useProfile, useProducts, useWeights, useUpdateWeights,
  useNotifications, useUpdateNotifications, useTestSlack,
  useDataStats, useUpdateProduct, useDeactivateProduct, useActivateProduct,
  useRotateKey, usePurgeProduct, useDeleteProduct, useCreateProduct,
} from '@/hooks/use-products';
import { relativeTime, formatNumber } from '@/lib/format';
import type { Product, HealthWeights } from '@/lib/types';

// ─── Tab keys ───
const TABS = [
  { key: 'profile', label: 'Profile', group: 'account' },
  { key: 'team', label: 'Team', group: 'account' },
  { key: 'products', label: 'Products', group: 'config' },
  { key: 'weights', label: 'Health Score', group: 'config' },
  { key: 'maturity', label: 'Maturity', group: 'config' },
  { key: 'notifications', label: 'Notifications', group: 'config' },
  { key: 'data', label: 'Data', group: 'config' },
  { key: 'danger', label: 'Danger Zone', group: 'danger' },
] as const;

type TabKey = typeof TABS[number]['key'];

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const activeTab = (params.get('tab') as TabKey) || 'profile';

  const setTab = useCallback((tab: TabKey) => {
    setParams({ tab }, { replace: true });
  }, [setParams]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-text-secondary text-[0.82rem] mt-1">Your account, team, products, and system configuration</p>
      </div>

      <div className="flex gap-8 min-h-[600px]">
        {/* Left Nav */}
        <nav className="w-[200px] shrink-0 hidden lg:block" aria-label="Settings">
          <div className="sticky top-8 flex flex-col gap-px" role="tablist" aria-orientation="vertical">
            {TABS.map((t, i) => {
              const prevGroup = i > 0 ? TABS[i - 1].group : null;
              const showDivider = prevGroup && prevGroup !== t.group;
              return (
                <div key={t.key}>
                  {showDivider && <div className="h-px bg-border my-2" />}
                  <button
                    role="tab"
                    aria-selected={activeTab === t.key}
                    aria-controls={`panel-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`w-full text-left px-3 py-1.5 rounded-sm text-[0.8rem] font-medium transition-all ${
                      activeTab === t.key
                        ? 'bg-[rgba(0,144,144,0.12)] text-accent-bright'
                        : t.group === 'danger'
                          ? 'text-danger hover:bg-danger-soft'
                          : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                    }`}
                  >
                    {t.label}
                  </button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Mobile Tab Bar */}
        <div className="lg:hidden mb-4 -mt-2 w-full overflow-x-auto">
          <div className="flex gap-1 pb-2" role="tablist" aria-label="Settings">
            {TABS.map(t => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab === t.key}
                aria-controls={`panel-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[0.72rem] font-medium transition-all ${
                  activeTab === t.key
                    ? 'bg-accent-soft text-accent-bright'
                    : 'bg-bg-elevated text-text-secondary'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Panel Content */}
        <div className="flex-1 min-w-0" role="tabpanel" id={`panel-${activeTab}`} aria-label={TABS.find(t => t.key === activeTab)?.label}>
          {activeTab === 'profile' && <ProfilePanel />}
          {activeTab === 'team' && <TeamPanel />}
          {activeTab === 'products' && <ProductsPanel />}
          {activeTab === 'weights' && <WeightsPanel />}
          {activeTab === 'maturity' && <MaturityPanel />}
          {activeTab === 'notifications' && <NotificationsPanel />}
          {activeTab === 'data' && <DataPanel />}
          {activeTab === 'danger' && <DangerZonePanel />}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// PROFILE PANEL
// ════════════════════════════════════════════

function ProfilePanel() {
  const { data: profile, isLoading } = useProfile();

  if (isLoading) return <PanelSkeleton />;

  const initials = profile?.email
    ? profile.email.split('@')[0].slice(0, 2).toUpperCase()
    : '??';

  return (
    <PanelWrapper title="Profile" desc="Your account details from Cloudflare Access">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center font-display text-xl font-bold text-white">
          {initials}
        </div>
        <div>
          <div className="font-display text-lg font-bold">{profile?.email?.split('@')[0] || '—'}</div>
          <div className="text-[0.78rem] text-text-secondary">{profile?.email}</div>
        </div>
      </div>

      <InfoRow label="Email" value={profile?.email || '—'} />
      <InfoRow label="User ID" value={profile?.user_id || '—'} mono />
      <InfoRow label="Auth Provider" value={profile?.auth_provider || '—'} />
      <InfoRow label="Role" value="Super Admin" badge="accent" />
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// TEAM PANEL
// ════════════════════════════════════════════

function TeamPanel() {
  const { data: profile } = useProfile();

  const roles = [
    { role: 'Super Admin', view: true, edit: true, manage: true, danger: true },
    { role: 'Admin', view: true, edit: true, manage: true, danger: false },
    { role: 'Viewer', view: true, edit: false, manage: false, danger: false },
  ];

  return (
    <PanelWrapper title="Team" desc="Team members and role permissions">
      <div className="bg-bg-card border border-border rounded-md p-4 mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center font-display text-sm font-bold text-white">
            {profile?.email?.split('@')[0].slice(0, 2).toUpperCase() || '??'}
          </div>
          <div>
            <div className="text-[0.85rem] font-semibold">{profile?.email?.split('@')[0] || '—'}</div>
            <div className="text-[0.72rem] text-text-muted">{profile?.email}</div>
          </div>
          <span className="ml-auto text-[0.62rem] font-bold uppercase tracking-wider bg-accent-soft text-accent-bright px-2 py-0.5 rounded-full border border-accent-border">
            Super Admin
          </span>
        </div>
      </div>

      <h4 className="text-[0.78rem] font-semibold text-text-secondary mb-3">Role Permissions</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-[0.76rem]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-3 font-semibold text-text-secondary">Role</th>
              <th className="text-center py-2 px-3 font-semibold text-text-secondary">View</th>
              <th className="text-center py-2 px-3 font-semibold text-text-secondary">Edit Config</th>
              <th className="text-center py-2 px-3 font-semibold text-text-secondary">Manage Team</th>
              <th className="text-center py-2 px-3 font-semibold text-text-secondary">Danger Zone</th>
            </tr>
          </thead>
          <tbody>
            {roles.map(r => (
              <tr key={r.role} className="border-b border-border/50">
                <td className="py-2 px-3 font-medium">{r.role}</td>
                <td className="text-center py-2 px-3">{r.view ? '✓' : '—'}</td>
                <td className="text-center py-2 px-3">{r.edit ? '✓' : '—'}</td>
                <td className="text-center py-2 px-3">{r.manage ? '✓' : '—'}</td>
                <td className="text-center py-2 px-3">{r.danger ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-bg-elevated border border-border rounded-md p-4">
        <div className="text-[0.78rem] font-semibold text-text-secondary mb-1">Access Control</div>
        <p className="text-[0.72rem] text-text-muted">
          Team access is managed through Cloudflare Access SSO. Members with access to the VyraDash application
          are automatically granted Viewer permissions. Promote to Admin or Super Admin via this panel.
        </p>
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// PRODUCTS PANEL
// ════════════════════════════════════════════

function ProductsPanel() {
  const { data: products, isLoading } = useProducts();
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<{ slug: string; key: string } | null>(null);

  if (isLoading) return <PanelSkeleton />;

  return (
    <PanelWrapper title="Products" desc="Registered ecosystem products and API key management">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[0.72rem] text-text-muted">{products?.length || 0} products registered</span>
        <button
          onClick={() => setShowCreate(true)}
          className="px-3 py-1.5 rounded-md bg-accent text-white text-[0.76rem] font-semibold hover:bg-accent-bright transition-colors"
        >
          + Register Product
        </button>
      </div>

      {showCreate && <CreateProductForm onClose={() => setShowCreate(false)} />}

      {newKey && (
        <div className="bg-success-soft border border-success/20 rounded-md p-4 mb-4">
          <div className="text-[0.78rem] font-semibold text-success mb-1">New API Key for {newKey.slug}</div>
          <code className="text-[0.72rem] font-mono bg-bg-elevated px-2 py-1 rounded text-accent-bright break-all select-all">
            {newKey.key}
          </code>
          <p className="text-[0.68rem] text-text-muted mt-2">Save this key now — it will not be shown again.</p>
          <button onClick={() => setNewKey(null)} className="text-[0.68rem] text-text-muted underline mt-1">Dismiss</button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {products?.map(p => (
          <ProductCard key={p.id} product={p} onNewKey={(slug, key) => setNewKey({ slug, key })} />
        ))}
      </div>
    </PanelWrapper>
  );
}

function ProductCard({ product: p, onNewKey }: { product: Product; onNewKey: (slug: string, key: string) => void }) {
  const rotate = useRotateKey();
  const deactivate = useDeactivateProduct();
  const activate = useActivateProduct();
  const isInactive = p.status === 'inactive';

  return (
    <div className={`bg-bg-card border border-border rounded-md p-4 ${isInactive ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${isInactive ? 'bg-text-muted' : p.health_score && p.health_score >= 85 ? 'bg-success' : p.health_score && p.health_score >= 70 ? 'bg-warning' : 'bg-danger'}`} />
          <span className="font-display text-[0.92rem] font-bold">{p.name}</span>
        </div>
        <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
          isInactive ? 'bg-bg-elevated text-text-muted' : 'bg-success-soft text-success border border-success/20'
        }`}>
          {p.status}
        </span>
      </div>

      {p.description && <p className="text-[0.72rem] text-text-muted mb-2">{p.description}</p>}

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[0.72rem] text-text-secondary mb-3">
        <div><span className="text-text-muted">Slug:</span> <code className="text-accent-bright">{p.slug}</code></div>
        <div><span className="text-text-muted">Registered:</span> {new Date(p.created_at).toLocaleDateString()}</div>
        {p.repo_url && <div className="col-span-2"><span className="text-text-muted">Repo:</span> {p.repo_url}</div>}
        <div><span className="text-text-muted">Last updated:</span> {relativeTime(p.updated_at)}</div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={async () => {
            const res = await rotate.mutateAsync(p.slug);
            onNewKey(p.slug, res.data.api_key);
          }}
          disabled={rotate.isPending}
          className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-text-secondary border border-border hover:border-accent-border hover:text-accent-bright transition-colors disabled:opacity-50"
        >
          {rotate.isPending ? 'Rotating...' : 'Rotate Key'}
        </button>
        {isInactive ? (
          <button
            onClick={() => activate.mutate(p.slug)}
            disabled={activate.isPending}
            className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-success-soft text-success border border-success/20 hover:bg-success/20 transition-colors disabled:opacity-50"
          >
            Activate
          </button>
        ) : (
          <button
            onClick={() => deactivate.mutate(p.slug)}
            disabled={deactivate.isPending}
            className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-text-muted border border-border hover:border-warning/30 hover:text-warning transition-colors disabled:opacity-50"
          >
            Deactivate
          </button>
        )}
      </div>
    </div>
  );
}

function CreateProductForm({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [desc, setDesc] = useState('');
  const [repo, setRepo] = useState('');
  const create = useCreateProduct();
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const res = await create.mutateAsync({
      name, slug,
      ...(desc && { description: desc }),
      ...(repo && { repo_url: repo }),
    });
    setCreatedKey(res.data.api_key);
  };

  if (createdKey) {
    return (
      <div className="bg-bg-card border border-accent-border rounded-md p-5 mb-4">
        <div className="text-[0.85rem] font-semibold text-success mb-2">Product Registered</div>
        <p className="text-[0.72rem] text-text-secondary mb-3">Your API key (shown once):</p>
        <code className="block bg-bg-elevated px-3 py-2 rounded font-mono text-[0.72rem] text-accent-bright break-all select-all mb-3">
          {createdKey}
        </code>
        <button onClick={onClose} className="px-3 py-1.5 rounded bg-accent text-white text-[0.72rem] font-semibold">Done</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-bg-card border border-border rounded-md p-5 mb-4">
      <div className="text-[0.85rem] font-semibold mb-4">Register New Product</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="text-[0.68rem] text-text-muted font-medium">Name</span>
          <input value={name} onChange={e => setName(e.target.value)} required className="input-field mt-1" placeholder="VyraTri" />
        </label>
        <label className="block">
          <span className="text-[0.68rem] text-text-muted font-medium">Slug</span>
          <input value={slug} onChange={e => setSlug(e.target.value)} required pattern="^[a-z0-9-]+$" className="input-field mt-1" placeholder="vyratri" />
        </label>
      </div>
      <label className="block mb-3">
        <span className="text-[0.68rem] text-text-muted font-medium">Description</span>
        <input value={desc} onChange={e => setDesc(e.target.value)} className="input-field mt-1" placeholder="Optional description" />
      </label>
      <label className="block mb-4">
        <span className="text-[0.68rem] text-text-muted font-medium">Repository URL</span>
        <input value={repo} onChange={e => setRepo(e.target.value)} type="url" className="input-field mt-1" placeholder="https://github.com/..." />
      </label>
      <div className="flex gap-2">
        <button type="submit" disabled={create.isPending} className="px-3 py-1.5 rounded bg-accent text-white text-[0.72rem] font-semibold hover:bg-accent-bright transition-colors disabled:opacity-50">
          {create.isPending ? 'Registering...' : 'Register'}
        </button>
        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded bg-bg-elevated text-text-secondary text-[0.72rem] font-medium border border-border hover:text-text-primary transition-colors">
          Cancel
        </button>
      </div>
      {create.isError && <p className="text-[0.68rem] text-danger mt-2">{(create.error as Error).message}</p>}
    </form>
  );
}

// ════════════════════════════════════════════
// HEALTH SCORE WEIGHTS PANEL
// ════════════════════════════════════════════

const DIMENSION_LABELS: Record<keyof HealthWeights, string> = {
  test_pass_rate: 'Test Pass Rate',
  security: 'Security',
  accessibility: 'Accessibility',
  deploy_freshness: 'Deploy Freshness',
  gate_health: 'Gate Health',
  code_coverage: 'Code Coverage',
  performance: 'Performance',
  cross_browser: 'Cross-Browser',
  bundle_size: 'Bundle Size',
};

function WeightsPanel() {
  const { data: config, isLoading } = useWeights();
  const update = useUpdateWeights();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<HealthWeights | null>(null);

  if (isLoading || !config) return <PanelSkeleton />;

  const weights = editing && draft ? draft : config.weights;
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);

  const startEdit = () => {
    setDraft({ ...config.weights });
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!draft || sum !== 100) return;
    await update.mutateAsync({ weights: draft, thresholds: config.thresholds });
    setEditing(false);
    setDraft(null);
  };

  const setWeight = (key: keyof HealthWeights, val: number) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: Math.max(0, Math.min(100, val)) });
  };

  return (
    <PanelWrapper title="Health Score Weights" desc="Configure how each quality dimension contributes to the overall health score">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`text-[0.85rem] font-display font-bold ${sum === 100 ? 'text-success' : 'text-danger'}`}>
            {sum}/100
          </span>
          <span className="text-[0.68rem] text-text-muted">total weight</span>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="px-3 py-1.5 rounded bg-accent text-white text-[0.72rem] font-semibold hover:bg-accent-bright transition-colors">
            Edit Weights
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={cancelEdit} className="px-3 py-1.5 rounded bg-bg-elevated text-text-secondary text-[0.72rem] font-medium border border-border">
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={sum !== 100 || update.isPending}
              className="px-3 py-1.5 rounded bg-accent text-white text-[0.72rem] font-semibold hover:bg-accent-bright transition-colors disabled:opacity-50"
            >
              {update.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {(Object.keys(DIMENSION_LABELS) as (keyof HealthWeights)[]).map(key => {
          const val = weights[key];
          return (
            <div key={key} className="flex items-center gap-3">
              <span className="w-[140px] text-[0.76rem] text-text-secondary shrink-0">{DIMENSION_LABELS[key]}</span>
              <div className="flex-1 h-2 bg-bg-elevated rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent rounded-full transition-all duration-200"
                  style={{ width: `${val}%` }}
                />
              </div>
              {editing ? (
                <input
                  type="number"
                  min={0} max={100}
                  value={val}
                  onChange={e => setWeight(key, parseInt(e.target.value) || 0)}
                  className="w-14 bg-bg-elevated border border-border rounded px-2 py-0.5 text-[0.76rem] text-center font-mono text-accent-bright focus:outline-none focus:border-accent"
                />
              ) : (
                <span className="w-14 text-right text-[0.76rem] font-mono text-accent-bright">{val}%</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <h4 className="text-[0.78rem] font-semibold text-text-secondary mb-3">Color Thresholds</h4>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-success" />
            <span className="text-[0.72rem] text-text-secondary">Green: ≥ {config.thresholds.green}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-warning" />
            <span className="text-[0.72rem] text-text-secondary">Yellow: {config.thresholds.yellow}–{config.thresholds.green - 1}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-danger" />
            <span className="text-[0.72rem] text-text-secondary">Red: &lt; {config.thresholds.yellow}</span>
          </div>
        </div>
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// MATURITY REFERENCE PANEL
// ════════════════════════════════════════════

function MaturityPanel() {
  const tiers = [
    {
      name: 'Bronze', color: '#D97706', bg: 'rgba(217,119,6,0.12)', border: 'rgba(217,119,6,0.2)',
      criteria: ['Health score ≥ 50', 'At least 1 test run in 14 days', 'TypeScript strict mode', 'ESLint active', '≥ 50 tests', '≥ 1 E2E test', 'Deployed at least once'],
    },
    {
      name: 'Silver', color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.2)',
      criteria: ['Health score ≥ 75', '8/8 quality gates passing', '≥ 500 tests', '3+ browser E2E tests', 'axe-core accessibility tests', '0 prod critical/high vulns', 'Deployed within 14 days', 'Lighthouse A11y ≥ 95'],
    },
    {
      name: 'Gold', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.2)',
      criteria: ['Health score ≥ 90', '11 test layers active', '≥ 2,000 tests', 'Visual regression tests', '10+ resilience tests', 'Lighthouse A11y = 100, Perf ≥ 85', '0 prod vulnerabilities', 'Weekly deploys', 'Lead time < 1 hour', '90 days of health data'],
    },
  ];

  return (
    <PanelWrapper title="Maturity Model" desc="Quality maturity tier criteria — read-only reference">
      <div className="flex flex-col gap-4">
        {tiers.map(t => (
          <div key={t.name} className="rounded-md p-4 border" style={{ background: t.bg, borderColor: t.border }}>
            <div className="font-display text-[0.92rem] font-bold mb-3" style={{ color: t.color }}>{t.name}</div>
            <ul className="flex flex-col gap-1.5">
              {t.criteria.map(c => (
                <li key={c} className="flex items-start gap-2 text-[0.76rem] text-text-secondary">
                  <span className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[0.6rem] shrink-0" style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}>✓</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// NOTIFICATIONS PANEL
// ════════════════════════════════════════════

function NotificationsPanel() {
  const { data: config, isLoading } = useNotifications();
  const update = useUpdateNotifications();
  const testSlack = useTestSlack();
  const [webhookInput, setWebhookInput] = useState('');
  const [showWebhookEdit, setShowWebhookEdit] = useState(false);

  if (isLoading || !config) return <PanelSkeleton />;

  const alertTypes = [
    { key: 'health_drop' as const, label: 'Health Score Drop', desc: 'When a product health score drops significantly' },
    { key: 'gate_failure' as const, label: 'Gate Failure', desc: 'When a quality gate fails on a test run' },
    { key: 'new_vuln' as const, label: 'New Vulnerability', desc: 'When a new critical/high vulnerability is detected' },
    { key: 'deploy_failure' as const, label: 'Deploy Failure', desc: 'When a deployment fails' },
    { key: 'stale_product' as const, label: 'Stale Product', desc: 'When a product has no ingest for 14+ days' },
  ];

  const saveWebhook = async () => {
    await update.mutateAsync({ slack_webhook_url: webhookInput || null });
    setShowWebhookEdit(false);
  };

  const toggleAlert = (key: keyof typeof config.alerts) => {
    update.mutate({ alerts: { [key]: !config.alerts[key] } });
  };

  return (
    <PanelWrapper title="Notifications" desc="Slack integration and alert preferences">
      <div className="bg-bg-card border border-border rounded-md p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[0.78rem] font-semibold">Slack Webhook</div>
          <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
            config.slack_connected
              ? 'bg-success-soft text-success border border-success/20'
              : 'bg-bg-elevated text-text-muted border border-border'
          }`}>
            {config.slack_connected ? 'Connected' : 'Not connected'}
          </span>
        </div>
        {config.slack_connected && !showWebhookEdit && (
          <div className="flex items-center gap-2 mb-2">
            <code className="text-[0.72rem] font-mono text-text-muted">{config.slack_webhook_masked}</code>
          </div>
        )}
        {showWebhookEdit ? (
          <div className="flex gap-2 mt-2">
            <input
              value={webhookInput}
              onChange={e => setWebhookInput(e.target.value)}
              placeholder="https://hooks.slack.com/services/..."
              className="input-field flex-1"
            />
            <button onClick={saveWebhook} disabled={update.isPending} className="px-3 py-1.5 rounded bg-accent text-white text-[0.72rem] font-semibold disabled:opacity-50">
              Save
            </button>
            <button onClick={() => setShowWebhookEdit(false)} className="px-3 py-1.5 rounded bg-bg-elevated text-text-secondary text-[0.72rem] border border-border">
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex gap-2 mt-2">
            <button onClick={() => setShowWebhookEdit(true)} className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-text-secondary border border-border hover:text-text-primary transition-colors">
              {config.slack_connected ? 'Update' : 'Configure'}
            </button>
            {config.slack_connected && (
              <button
                onClick={() => testSlack.mutate()}
                disabled={testSlack.isPending}
                className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-text-secondary border border-border hover:text-accent-bright hover:border-accent-border transition-colors disabled:opacity-50"
              >
                {testSlack.isPending ? 'Sending...' : 'Test'}
              </button>
            )}
          </div>
        )}
        {testSlack.isSuccess && <p className="text-[0.68rem] text-success mt-2">Test notification sent successfully.</p>}
        {testSlack.isError && <p className="text-[0.68rem] text-danger mt-2">{(testSlack.error as Error).message}</p>}
      </div>

      <h4 className="text-[0.78rem] font-semibold text-text-secondary mb-3">Alert Types</h4>
      <div className="flex flex-col gap-2">
        {alertTypes.map(a => (
          <div key={a.key} className="flex items-center justify-between bg-bg-card border border-border rounded-md px-4 py-3">
            <div>
              <div className="text-[0.78rem] font-medium">{a.label}</div>
              <div className="text-[0.68rem] text-text-muted">{a.desc}</div>
            </div>
            <button
              onClick={() => toggleAlert(a.key)}
              className={`w-10 h-5 rounded-full relative transition-colors ${config.alerts[a.key] ? 'bg-accent' : 'bg-bg-elevated border border-border'}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${config.alerts[a.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        ))}
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// DATA OVERVIEW PANEL
// ════════════════════════════════════════════

function DataPanel() {
  const { data: stats, isLoading } = useDataStats();

  if (isLoading || !stats) return <PanelSkeleton />;

  const items = [
    { label: 'Test Runs', value: stats.test_runs },
    { label: 'Gate Results', value: stats.gate_results },
    { label: 'Lighthouse Scores', value: stats.lighthouse_scores },
    { label: 'Deploys', value: stats.deploys },
    { label: 'Security Scans', value: stats.security_scans },
    { label: 'Health Snapshots', value: stats.health_snapshots },
  ];

  return (
    <PanelWrapper title="Data Overview" desc="Storage statistics and retention configuration">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {items.map(i => (
          <div key={i.label} className="bg-bg-card border border-border rounded-md p-4 text-center">
            <div className="font-display text-xl font-bold text-accent-bright">{formatNumber(i.value)}</div>
            <div className="text-[0.68rem] text-text-muted mt-0.5">{i.label}</div>
          </div>
        ))}
      </div>

      <h4 className="text-[0.78rem] font-semibold text-text-secondary mb-3">Retention Policy</h4>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between bg-bg-card border border-border rounded-md px-4 py-3">
          <div className="text-[0.78rem] text-text-secondary">Health Snapshots</div>
          <div className="text-[0.78rem] font-mono text-accent-bright">365 days</div>
        </div>
        <div className="flex items-center justify-between bg-bg-card border border-border rounded-md px-4 py-3">
          <div className="text-[0.78rem] text-text-secondary">Test Runs & Gate Results</div>
          <div className="text-[0.78rem] font-mono text-text-muted">No limit</div>
        </div>
        <div className="flex items-center justify-between bg-bg-card border border-border rounded-md px-4 py-3">
          <div className="text-[0.78rem] text-text-secondary">Deploys & Security Scans</div>
          <div className="text-[0.78rem] font-mono text-text-muted">No limit</div>
        </div>
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// DANGER ZONE PANEL
// ════════════════════════════════════════════

function DangerZonePanel() {
  const { data: products } = useProducts();
  const [action, setAction] = useState<{ type: 'purge' | 'delete'; slug: string } | null>(null);
  const [confirmInput, setConfirmInput] = useState('');
  const purge = usePurgeProduct();
  const remove = useDeleteProduct();

  const activeProducts = products?.filter(p => p.status === 'active') || [];

  const execute = async () => {
    if (!action || confirmInput !== action.slug) return;
    if (action.type === 'purge') {
      await purge.mutateAsync({ slug: action.slug });
    } else {
      await remove.mutateAsync({ slug: action.slug });
    }
    setAction(null);
    setConfirmInput('');
  };

  return (
    <PanelWrapper title="Danger Zone" desc="Destructive operations — these cannot be undone">
      <div className="flex flex-col gap-4">
        <div className="bg-danger-soft border border-danger/20 rounded-md p-4">
          <div className="text-[0.85rem] font-semibold text-danger mb-1">Purge Product Data</div>
          <p className="text-[0.72rem] text-text-secondary mb-3">
            Delete all test runs, gate results, lighthouse scores, deploys, security scans, and health snapshots for a product.
            The product registration stays intact. Health score resets to 0.
          </p>
          <div className="flex gap-2 flex-wrap">
            {activeProducts.map(p => (
              <button
                key={p.slug}
                onClick={() => { setAction({ type: 'purge', slug: p.slug }); setConfirmInput(''); }}
                className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-danger border border-danger/20 hover:bg-danger/10 transition-colors"
              >
                Purge {p.name}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-danger-soft border border-danger/20 rounded-md p-4">
          <div className="text-[0.85rem] font-semibold text-danger mb-1">Delete Product Permanently</div>
          <p className="text-[0.72rem] text-text-secondary mb-3">
            Permanently delete a product and ALL associated data. This cannot be undone.
            The API key will stop working immediately.
          </p>
          <div className="flex gap-2 flex-wrap">
            {(products || []).map(p => (
              <button
                key={p.slug}
                onClick={() => { setAction({ type: 'delete', slug: p.slug }); setConfirmInput(''); }}
                className="px-2.5 py-1 rounded text-[0.68rem] font-medium bg-bg-elevated text-danger border border-danger/20 hover:bg-danger/10 transition-colors"
              >
                Delete {p.name}
              </button>
            ))}
          </div>
        </div>

        {action && (
          <div className="bg-bg-card border-2 border-danger rounded-md p-5">
            <div className="text-[0.85rem] font-semibold text-danger mb-2">
              Confirm {action.type === 'purge' ? 'Purge' : 'Delete'}
            </div>
            <p className="text-[0.76rem] text-text-secondary mb-3">
              Type <code className="text-danger font-mono">{action.slug}</code> to confirm:
            </p>
            <input
              value={confirmInput}
              onChange={e => setConfirmInput(e.target.value)}
              className="input-field mb-3 border-danger/30 focus:border-danger"
              placeholder={action.slug}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={execute}
                disabled={confirmInput !== action.slug || purge.isPending || remove.isPending}
                className="px-3 py-1.5 rounded bg-danger text-white text-[0.72rem] font-semibold disabled:opacity-50"
              >
                {purge.isPending || remove.isPending ? 'Processing...' : action.type === 'purge' ? 'Purge All Data' : 'Delete Permanently'}
              </button>
              <button onClick={() => setAction(null)} className="px-3 py-1.5 rounded bg-bg-elevated text-text-secondary text-[0.72rem] border border-border">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </PanelWrapper>
  );
}

// ════════════════════════════════════════════
// SHARED COMPONENTS
// ════════════════════════════════════════════

function PanelWrapper({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="animate-[fadeSlideIn_0.2s_ease-out]">
      <div className="mb-5">
        <h2 className="font-display text-lg font-bold">{title}</h2>
        <p className="text-[0.72rem] text-text-muted mt-0.5">{desc}</p>
      </div>
      {children}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-6 w-48 bg-bg-elevated rounded mb-2" />
      <div className="h-3 w-80 bg-bg-elevated rounded mb-6" />
      <div className="h-32 bg-bg-elevated rounded mb-3" />
      <div className="h-32 bg-bg-elevated rounded" />
    </div>
  );
}

function InfoRow({ label, value, mono, badge }: { label: string; value: string; mono?: boolean; badge?: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50">
      <span className="text-[0.76rem] text-text-muted">{label}</span>
      {badge ? (
        <span className={`text-[0.62rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent-soft text-accent-bright border border-accent-border`}>
          {value}
        </span>
      ) : (
        <span className={`text-[0.76rem] text-text-secondary ${mono ? 'font-mono text-[0.68rem]' : ''}`}>{value}</span>
      )}
    </div>
  );
}
